// ---------------------------------------------------------------------------
// MP11 Phase 2 — Client-side delete-eligibility mirrors.
//
// These are UI courtesy checks so the "Delete record" button visibly shows
// its disabled reason without a wasted round-trip. Backend RBAC in
// server.py is the source of truth — helpers here are ADVISORY only. If
// the two ever diverge, the server's 403 response wins.
// ---------------------------------------------------------------------------

function eq(a, b) {
  return (a || "").toString().trim().toLowerCase() === (b || "").toString().trim().toLowerCase();
}

function isSuper(user) {
  return user?.role === "super_admin";
}

export function demoDeleteEligibility(demo, user) {
  if (isSuper(user)) return { allowed: true, reason: "" };
  const intakeSource = (demo?.intake_source || "").trim();
  if (intakeSource !== "internal_admin") {
    return {
      allowed: false,
      reason: "This demo was submitted from the public form. Only a Super Admin can delete it.",
    };
  }
  if (!eq(user?.email, demo?.created_by_email)) {
    return {
      allowed: false,
      reason: "Only the original creator or a Super Admin may delete this demo.",
    };
  }
  if ((demo?.workflow_status || "") !== "Demo Received") {
    return {
      allowed: false,
      reason: "This demo has advanced past the initial state. Only a Super Admin can delete it now.",
    };
  }
  if ((demo?.derived_track_id || "").trim()) {
    return {
      allowed: false,
      reason: "This demo has already been promoted to a track. Only a Super Admin can delete it.",
    };
  }
  if (demo?.compliance_hold?.placed) {
    return {
      allowed: false,
      reason: "This demo is under a compliance hold. Only a Super Admin can delete it.",
    };
  }
  const cps = demo?.contract_packet_status || "";
  if (cps && cps !== "Not Required Yet") {
    return {
      allowed: false,
      reason: "This demo has active contract activity. Only a Super Admin can delete it.",
    };
  }
  if ((demo?.received_docs || []).length > 0) {
    return {
      allowed: false,
      reason: "This demo has received documents. Only a Super Admin can delete it.",
    };
  }
  return { allowed: true, reason: "" };
}

export function submissionDeleteEligibility(sub, user) {
  if (isSuper(user)) return { allowed: true, reason: "" };
  if (!eq(user?.email, sub?.prepared_by)) {
    return {
      allowed: false,
      reason: "Only the original creator or a Super Admin may delete this submission.",
    };
  }
  if ((sub?.submission_status || "") !== "Draft") {
    return {
      allowed: false,
      reason: "This submission has advanced past Draft. Only a Super Admin can delete it.",
    };
  }
  if ((sub?.deal_status || "").trim()) {
    return {
      allowed: false,
      reason: "This submission has recorded deal activity. Only a Super Admin can delete it.",
    };
  }
  if ((sub?.result_status || "").trim()) {
    return {
      allowed: false,
      reason: "This submission has a recorded result. Only a Super Admin can delete it.",
    };
  }
  return { allowed: true, reason: "" };
}

export function licenseRequestDeleteEligibility(lr, user) {
  if (isSuper(user)) return { allowed: true, reason: "" };
  if (!eq(user?.email, lr?.created_by)) {
    return {
      allowed: false,
      reason: "Only the original creator or a Super Admin may delete this license request.",
    };
  }
  if ((lr?.request_status || "") !== "New") {
    return {
      allowed: false,
      reason: "This request has advanced past New. Only a Super Admin can delete it.",
    };
  }
  if ((lr?.linked_submission_id || "").trim()) {
    return {
      allowed: false,
      reason: "This request is linked to a submission. Only a Super Admin can delete it.",
    };
  }
  if ((lr?.assigned_reviewer || "").trim()) {
    return {
      allowed: false,
      reason: "This request has an assigned reviewer. Only a Super Admin can delete it.",
    };
  }
  return { allowed: true, reason: "" };
}

export function consultationDeleteEligibility(_cons, user) {
  if (isSuper(user)) return { allowed: true, reason: "" };
  return {
    allowed: false,
    reason: "Consultation requests can only be deleted by a Super Admin.",
  };
}
